import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';

// `Camera.getForwardRay` constructs a `Ray`, which is shaken out of the bundle unless
// something imports it for its side effect (CLAUDE.md: "Babylon features need explicit
// imports — and so do the Scene methods they call"). `XrManager.ts` already imports this
// for the same reason; imported here too rather than relied upon, because this module must
// not depend on another module's own import order to function.
import '@babylonjs/core/Culling/ray';

import type { ToolMode } from '../possession/ToolMode';
import { bindingsForHand } from './ControlBindings';
import type { Grabber, GrabberHub } from './Grabbers';
import type { HandRole } from './Handedness';
import { ControlHintsPanel } from './ControlHintsPanel';

/**
 * "Show what the controls do, when you look at your controller." CLAUDE.md's task, the
 * interaction half: turn a controller toward your face and a panel appears beside it, listing
 * `ControlBindings.bindingsForHand` for whichever hand and mode are currently in effect; look
 * away and it fades. `ControlHintsPanel` (the mesh, position, paint) and `ControlBindings`
 * (what the words say) both know nothing about *when* to show anything — that decision, the
 * look-at gesture itself, lives entirely here.
 *
 * **Look-at is the right trigger because it needs no binding to learn** (CLAUDE.md's own
 * framing for this task) — you cannot press a help button you have not found yet. The
 * detection stays deliberately simple: the angle between where the camera is looking and the
 * direction to the controller, held for a moment before the panel appears, with a second,
 * looser angle and a short grace period before it fades — hysteresis so a controller sitting
 * right at the edge of the cone does not flicker the panel on and off, not a second control
 * scheme. Every threshold below is a named constant, one line each, per the brief: "the owner
 * does not want sliders."
 *
 * **Per-hand.** The dominant and off hands do different things (`ControlBindings`), so which
 * grabber is currently being looked at decides which hand's bindings are shown — resolved
 * through `grabber.role`, the same `Handedness.ts` lookup every other gesture in this project
 * reads instead of asking which *physical* hand it is (CLAUDE.md: "Nothing outside the binding
 * layer asks which physical hand it has"). Left-handed mode needs no second code path here:
 * `role` already reflects it.
 *
 * **Anchored to whichever controller is being looked at, never to the camera** — CLAUDE.md:
 * "Nothing is welded to the camera inside a session." `ModeRingRenderer`'s own mode carousel is
 * the shipped precedent for a hand-anchored plate; this follows the same shape.
 */

/**
 * Degrees from the camera's forward direction within which a controller counts as "looked at"
 * for the purpose of *starting* the hold timer. Wide enough that a controller does not have to
 * sit dead centre in view — WebXR headsets report gaze direction, not eye direction, so a
 * controller held slightly to one side while looking generally at it should still qualify.
 */
const LOOK_ANGLE_SHOW_DEG = 20;

/**
 * Degrees from centre past which a *currently shown* panel counts as looked away from — wider
 * than `LOOK_ANGLE_SHOW_DEG`, deliberately, so a controller sitting right at the edge of the
 * show cone does not flicker the panel on and off as the angle wobbles a degree either side of
 * one single threshold.
 */
const LOOK_ANGLE_HIDE_DEG = 32;

/** How long the angle must stay within `LOOK_ANGLE_SHOW_DEG` before the panel appears — "a
 *  hold so it does not flicker" (CLAUDE.md's task brief), not an instant reaction to a glance
 *  that happened to sweep across the controller on its way somewhere else. */
const LOOK_HOLD_MS = 350;

/** How long the angle must stay past `LOOK_ANGLE_HIDE_DEG` before the panel starts fading —
 *  the show-side hold's own mirror, so a brief head turn away and back does not restart the
 *  hold from zero. */
const LOOK_AWAY_GRACE_MS = 250;

/** How long the fade-out itself takes, once the grace period above has elapsed. */
const FADE_MS = 300;

/** Anything closer than this, in metres, is treated as too close to have a meaningful look
 *  direction toward it (a hand right at the eye, or a degenerate zero-length grabber position
 *  before tracking has ever reported one) — guards the direction vector below from a
 *  near-zero length rather than trusting every grabber position to already be sane. */
const MIN_LOOK_DISTANCE_M = 0.05;

/**
 * How far below eye height (`eye.y - grabber.position.y`, in metres) a controller must sit
 * before it qualifies as "looked at" — CLAUDE.md task brief: "needs to be limited to when you
 * look down at the controller and not just when you look at it. Made it hard to write when it
 * popped up over the marker." Drawing holds the controller up near the face, roughly level with
 * the eye, which already satisfies the *angle* check above (the board is straight ahead, same
 * as the hand); nothing about angle alone can tell "reading the controller" apart from
 * "writing with it in front of your face." Height can.
 *
 * Gated on the controller's own height relative to the head, not on head pitch, deliberately:
 * pitch measures how much the *neck* is bent, which is a proxy for "is the controller below my
 * eyes" that breaks in both directions a real player can be in. A seated player's head sits
 * lower in the room but the controller sits proportionally lower too, so the *relative* height
 * this constant checks is unaffected by standing versus sitting, where an absolute pitch
 * threshold tuned for a standing player would trigger too early for one sitting down with their
 * hands in their lap. And a player who lowers a controller to their side while still looking
 * straight ahead (no pitch at all) still has a controller that is genuinely below eye level —
 * exactly the "check my controls" gesture this feature exists for — where a pitch gate would
 * refuse to show anything until they also tipped their head down, which is not the gesture
 * being asked for.
 */
const MIN_BELOW_EYE_M = 0.12;

interface Candidate {
  readonly grabber: Grabber;
  readonly angleDeg: number;
}

export class ControlHintsController {
  private readonly panel: ControlHintsPanel;

  /** The grabber id being held toward, and since when — cleared the instant it stops
   *  qualifying, so a look that wanders off before the hold completes restarts from zero. */
  private pendingId: string | null = null;
  private pendingSinceMs: number | null = null;

  /** The grabber id currently shown — null while hidden. `activeRole` is captured alongside it
   *  because the grabber can disappear (controller disconnects) while still fading out, at
   *  which point there is nothing left to re-read `role` from. */
  private activeId: string | null = null;
  private activeRole: HandRole = 'dominant';
  private lookAwaySinceMs: number | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly toolMode: ToolMode,
  ) {
    this.panel = new ControlHintsPanel(scene);
  }

  /** Whether the panel is currently shown at all (any opacity above zero) — exposed for
   *  tests. */
  get isVisible(): boolean {
    return this.activeId !== null;
  }

  /** Which grabber the panel is currently anchored to, or holding toward — null when nothing
   *  qualifies. Exposed for tests. */
  get targetId(): string | null {
    return this.activeId ?? this.pendingId;
  }

  /** Which hand's bindings the panel is currently showing, or was last showing while fading —
   *  null while nothing has ever been shown. Exposed for tests. */
  get targetRole(): HandRole | null {
    return this.activeId !== null ? this.activeRole : null;
  }

  /** What the panel most recently painted — see `ControlHintsPanel.lastContent`. Exposed for
   *  tests; nothing in this class reads it back. */
  get lastContent(): ControlHintsPanel['lastContent'] {
    return this.panel.lastContent;
  }

  /** Registered the same way every other per-frame gesture reader in this project is. */
  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick(performance.now()));
    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  private tick(nowMs: number): void {
    const camera = this.scene.activeCamera;

    if (!camera) {
      this.hideImmediately();
      return;
    }

    // Force-computed at the read site: an unforced computeWorldMatrix can return last frame's
    // answer to a reader inside onBeforeRenderObservable (CLAUDE.md). Grabber.position needs
    // no such force — GrabberHub writes it as a plain field every frame, not a mesh's cached
    // world matrix — but the camera's own forward/global position, read off its transform,
    // does. `Camera.computeWorldMatrix()` (unlike `TransformNode`'s) takes no `force` argument
    // and always recomputes, so there is nothing to pass.
    camera.computeWorldMatrix();
    const eye = camera.globalPosition;

    // `Camera` (unlike `TransformNode`) has no `.forward` getter — `getForwardRay` is the same
    // way `PaletteUI.positionTray` already reads the camera's own facing direction elsewhere in
    // this project.
    const forward = camera.getForwardRay(1).direction;

    const best = this.bestCandidate(eye, forward);

    // The viewer's own rightward direction, flattened to horizontal — see
    // `ControlHintsPanel.setPose`'s own doc for why the side offset has to be applied along
    // this rather than a fixed world axis. Left-handed, Y-up: for a player facing world +Z
    // (forward = (0,0,1)), this must evaluate to world +X, which is verified directly in
    // `ControlHintsController.test.ts`.
    const sideAxis = new Vector3(forward.z, 0, -forward.x).normalize();

    if (this.activeId !== null) {
      this.tickActive(best, nowMs, eye, sideAxis);
      return;
    }

    this.tickPending(best, nowMs, eye, sideAxis);
  }

  private bestCandidate(eye: Vector3, forward: Vector3): Candidate | null {
    let best: Candidate | null = null;

    for (const grabber of this.grabbers.grabbers) {
      // The desktop pointer has nothing to "look at" — it is the camera itself — and hand/
      // controller kinds are the only ones this gesture makes sense for.
      if (grabber.kind === 'pointer') {
        continue;
      }

      // Below eye level, not merely "in view" — see MIN_BELOW_EYE_M's own doc. Drawing holds
      // the controller up near the face, which the angle check alone cannot tell apart from
      // deliberately looking down at it to check the hints.
      if (eye.y - grabber.position.y < MIN_BELOW_EYE_M) {
        continue;
      }

      const toGrabber = grabber.position.subtract(eye);
      const distance = toGrabber.length();

      if (distance < MIN_LOOK_DISTANCE_M) {
        continue;
      }

      const angleDeg = angleBetweenDeg(forward, toGrabber.scale(1 / distance));

      if (best === null || angleDeg < best.angleDeg) {
        best = { grabber, angleDeg };
      }
    }

    return best;
  }

  private tickActive(best: Candidate | null, nowMs: number, eye: Vector3, sideAxis: Vector3): void {
    const activeGrabber =
      best?.grabber.id === this.activeId
        ? best.grabber
        : this.grabbers.grabbers.find((candidate) => candidate.id === this.activeId);

    const stillLooking = best !== null && best.grabber.id === this.activeId && best.angleDeg <= LOOK_ANGLE_HIDE_DEG;

    // Repainted from live state on *every* tick the panel is still shown at all — not only
    // while `stillLooking` holds — so content painted before a look-away never outlives the
    // mode it was painted for. The grace and fade branches below used to skip this (the fade
    // branch skipped it entirely), which meant a mode switch landing while the panel was mid-
    // fade kept showing the pre-switch mode's bindings for up to
    // LOOK_AWAY_GRACE_MS + FADE_MS (550ms) — exactly the "stays on the build-mode description"
    // report, reproduced directly: see ControlHintsController.test.ts, "content painted before
    // a look-away is not shown stale while fading."
    if (activeGrabber) {
      this.renderAndPose(activeGrabber, eye, sideAxis);
    }

    if (stillLooking) {
      this.lookAwaySinceMs = null;
      this.panel.setAlpha(1);
      return;
    }

    this.lookAwaySinceMs ??= nowMs;
    const elapsed = nowMs - this.lookAwaySinceMs;

    if (elapsed < LOOK_AWAY_GRACE_MS) {
      // Still within the grace period — hold at full opacity so a brief look-away does not
      // visibly fade anything yet. Content is already current, from the render above.
      return;
    }

    const fadeElapsed = elapsed - LOOK_AWAY_GRACE_MS;

    if (fadeElapsed >= FADE_MS) {
      this.hideImmediately();
      return;
    }

    this.panel.setAlpha(1 - fadeElapsed / FADE_MS);
  }

  private tickPending(best: Candidate | null, nowMs: number, eye: Vector3, sideAxis: Vector3): void {
    if (best === null || best.angleDeg > LOOK_ANGLE_SHOW_DEG) {
      this.pendingId = null;
      this.pendingSinceMs = null;
      return;
    }

    if (this.pendingId !== best.grabber.id) {
      this.pendingId = best.grabber.id;
      this.pendingSinceMs = nowMs;
      return;
    }

    const heldFor = nowMs - (this.pendingSinceMs ?? nowMs);

    if (heldFor < LOOK_HOLD_MS) {
      return;
    }

    this.activeId = best.grabber.id;
    this.activeRole = best.grabber.role;
    this.pendingId = null;
    this.pendingSinceMs = null;
    this.lookAwaySinceMs = null;
    this.renderAndPose(best.grabber, eye, sideAxis);
    this.panel.setAlpha(1);
  }

  private renderAndPose(grabber: Grabber, eye: Vector3, sideAxis: Vector3): void {
    this.activeRole = grabber.role;

    const bindings = bindingsForHand(this.toolMode.mode, grabber.role);
    this.panel.render(panelTitle(grabber.role, this.toolMode.mode), bindings);

    const sideSign = grabber.handedness === 'left' ? -1 : 1;
    this.panel.setPose(grabber.position, eye, sideSign, sideAxis);
  }

  private hideImmediately(): void {
    this.activeId = null;
    this.pendingId = null;
    this.pendingSinceMs = null;
    this.lookAwaySinceMs = null;
    this.panel.setAlpha(0);
  }

  dispose(): void {
    this.panel.dispose();
  }
}

function angleBetweenDeg(a: Vector3, b: Vector3): number {
  const dot = Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z));
  return (Math.acos(dot) * 180) / Math.PI;
}

function panelTitle(role: HandRole, mode: string): string {
  const hand = role === 'dominant' ? 'Dominant hand' : 'Off hand';
  return `${hand} — ${mode}`;
}
